import {
	ANSWER_SOURCE,
	DOC_TYPES,
	getDisplayableDocuments,
	getDisplayableQuestions,
	getValidationResult,
	TEDCApplicant,
	ValidationItem,
	VALIDATION_STATUS
} from "@smvtech/x-flux";
import { useMemo } from "react";

import DocumentListItem from "./DocumentListItem";

const OCR_SOURCE_TO_DOC_TYPE: Partial<Record<ANSWER_SOURCE, DOC_TYPES>> = {
	[ANSWER_SOURCE.PASSPORT_FRONT_OCR]: DOC_TYPES.PASSPORT_FRONT,
	[ANSWER_SOURCE.PASSPORT_BACK_OCR]: DOC_TYPES.PASSPORT_BACK
};

type Props = {
	activeApplicantData: TEDCApplicant;
	isDocumentsComplete: boolean;
	onDocumentsCompleted: () => void;
	onViewFormErrors?: () => void;
};

const DocumentsList = ({ activeApplicantData, isDocumentsComplete, onDocumentsCompleted, onViewFormErrors }: Props) => {
	const documents = useMemo(
		() =>
			getDisplayableDocuments(activeApplicantData.questions, [ANSWER_SOURCE.USER_INPUT, ANSWER_SOURCE.DOCUMENT]),
		[activeApplicantData.questions]
	);
	const questions = useMemo(
		() => getDisplayableQuestions(activeApplicantData.questions, [ANSWER_SOURCE.PASSPORT_FRONT_OCR]),
		[activeApplicantData.questions]
	);

	const docTypesWithFormErrors = useMemo(() => {
		const errored = new Set<DOC_TYPES>();
		activeApplicantData.questions.forEach((q) => {
			const docType = OCR_SOURCE_TO_DOC_TYPE[q.question.answer_source];
			if (!docType) return;
			const validation = getValidationResult(q);
			if (
				validation?.status === VALIDATION_STATUS.UNFIXABLE ||
				validation?.status === VALIDATION_STATUS.FIXABLE
			) {
				errored.add(docType);
			}
		});
		return errored;
	}, [activeApplicantData.questions]);

	const formErrorsByDocType = useMemo(() => {
		const map = new Map<DOC_TYPES, ValidationItem[]>();
		activeApplicantData.questions.forEach((q) => {
			const docType = OCR_SOURCE_TO_DOC_TYPE[q.question.answer_source];
			if (!docType) return;
			const validation = getValidationResult(q);
			if (
				!validation ||
				validation.status === VALIDATION_STATUS.PERFECT ||
				validation.status === VALIDATION_STATUS.MISSING
			)
				return;
			const items = [...(validation.fixable?.items ?? []), ...(validation.unfixable?.items ?? [])];
			if (items.length > 0) {
				map.set(docType, [...(map.get(docType) ?? []), ...items]);
			}
		});
		return map;
	}, [activeApplicantData.questions]);

	const renderItem = (document: (typeof documents)[number]) => (
		<DocumentListItem
			key={document.id}
			document={document}
			questions={questions}
			activeApplicantData={activeApplicantData}
			isDocumentsComplete={isDocumentsComplete}
			onDocumentsCompleted={onDocumentsCompleted}
			hasFormErrors={!!(document.question.doc_type && docTypesWithFormErrors.has(document.question.doc_type))}
			formErrorItems={
				document.question.doc_type ? (formErrorsByDocType.get(document.question.doc_type) ?? []) : []
			}
			onViewFormErrors={onViewFormErrors}
		/>
	);

	return <div className="flex flex-col gap-4 pb-28">{documents?.map(renderItem)}</div>;
};

export default DocumentsList;

